import React, { FC, useEffect, useState } from 'react'
import shallow from 'zustand/shallow'
import classNames from 'classnames'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Input, Radio, Checkbox, Tooltip } from 'antd'
import { Control, Controller, useFieldArray, useForm, useWatch, FieldErrors } from 'react-hook-form'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerInputNumber from '../PerInputNumber'
import PerButtonCircle from '../ButtonCircle'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { MaxCharactersWrapper } from '../../formik-component/MaxCharactersWrapper'
import { useTargetsModalStore } from './TargetsModalStore'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import { KeyResultDto, Currency, Country, ObjectiveTemplateDto, ObjectiveInput } from '../../../types'
import { TargetModalTargetType, ObjectiveExtended } from './types'
import './index.less'
import { TargetsModalAsterisk, TargetsModalMessage } from './TargetsModalMessageComponents'
import { TargetsTemplateSelect } from './TargetsTemplateSelect'
import { useTargetsTemplates } from './async'
import { isDefined } from '../../../types.helper'
import { round2digits } from '../../../modules/appraisal/appraisal-flow/meeting/util/paymentsUtil'
import RichTextBox from '../RichTextBox'

const MAX_CHARS = 4000

const { TextArea } = Input

const getTasksLength = (tasks: Array<KeyResultDto>) => tasks.map((task) => task.text).join('').length

const TaskCharacterCounter = ({ control }: { control: Control<ObjectiveInput> }) => {
  const { t } = useTranslation()
  const tasks = useWatch({ control, name: 'keyResults' })
  return !!tasks && !!tasks.length ? (
    <PerText fontSize="xs" color="secondary" align="right" mt="xxxs">
      {`${t('targetModal.totalOfCharInTasks')} ${getTasksLength(tasks)} / ${MAX_CHARS}`}
    </PerText>
  ) : null
}

export const TargetsModalFormNewPastTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { formatDateShort } = useDateTime(user)

  const [currentObjective, setNewTargetForm, modalProps] = useTargetsModalStore(
    (state) => [state.currentObjective, state.setNewTargetForm, state.modalProps],
    shallow,
  )

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data } = useTargetsTemplates(modalProps.employeeId!)
  const targetsTemplate: ObjectiveTemplateDto[] = data && data.getTargetsTemplates ? data.getTargetsTemplates : []

  const _targetType =
    !!currentObjective && currentObjective.targetAmount ? TargetModalTargetType.monetary : TargetModalTargetType.development
  const [targetType, setTargetType] = useState<TargetModalTargetType>(_targetType)

  const newTargetForm = useForm<ObjectiveExtended>({
    defaultValues: !!currentObjective
      ? {
          ...currentObjective,
          achievedAmountPercentage:
            !!currentObjective.targetAmount && (!!currentObjective.achievedAmount || currentObjective.achievedAmount === 0)
              ? Number(((currentObjective.achievedAmount / currentObjective.targetAmount) * 100).toFixed(2))
              : undefined,
          targetType: _targetType,
          markedAsDone: true,
        }
      : { markedAsDone: true },
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    unregister,
    watch,
    clearErrors,
  } = newTargetForm

  const { fields, remove, append, replace } = useFieldArray({
    name: 'keyResults',
    keyName: 'id',
    control,
  })

  const currency = modalProps.employeeCurrency || Currency.Eur

  watch(['targetAmount', 'achievedAmount', 'achievedAmountPercentage'])

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const hasValidSelection = e.currentTarget.selectionStart !== e.currentTarget.selectionEnd
    if (getTasksLength(getValues().keyResults) >= MAX_CHARS) {
      if (!hasValidSelection) {
        e.preventDefault()
      }
    }
  }

  const handleOnPaste = (e: React.ClipboardEvent) => {
    const currentChars = getTasksLength(getValues().keyResults)
    if (currentChars + e.clipboardData.getData('text/plain').length >= MAX_CHARS) {
      e.preventDefault()
    }
  }

  const hasError = (errors: FieldErrors<ObjectiveExtended>, fieldName: string, index: number) => {
    if (index > -1) {
      return !!errors?.keyResults?.[index]?.[fieldName]
    }
    return !!errors && !!errors[fieldName]
  }

  const calcThreshold = () => {
    const targetAmount = !!currentObjective && currentObjective.targetAmount ? currentObjective.targetAmount : 0
    const alreadyPlanned = !!modalProps.alreadyPlanned ? modalProps.alreadyPlanned : 0
    const threshold = targetAmount + alreadyPlanned
    return round2digits(threshold)
  }

  const calcRemainingBonus = (currentValue: number | null | undefined) => {
    const value = currentValue || 0
    return Number((calcThreshold() - value).toFixed(2))
  }

  const calcAchievedAmount = (targetAmount: number | null | undefined, achievedAmountPercentage: number | undefined) => {
    if (!!targetAmount && (!!achievedAmountPercentage || achievedAmountPercentage === 0)) {
      setValue('achievedAmount', Number(((achievedAmountPercentage / 100) * targetAmount).toFixed(2)))
    } else {
      setValue('achievedAmount', undefined)
    }
  }

  const calcAchievedAmountPercentage = (achievedAmount: number | null | undefined, targetAmount: number | null | undefined) => {
    if (!!targetAmount && (!!achievedAmount || achievedAmount === 0)) {
      setValue('achievedAmountPercentage', Number(((achievedAmount / targetAmount) * 100).toFixed(2)))
    } else {
      setValue('achievedAmountPercentage', undefined)
    }
  }

  const handleTargetAmountChange = () => {
    if (!getValues().targetAmount) {
      setValue('achievedAmount', undefined)
      setValue('achievedAmountPercentage', undefined)
    } else {
      if (!!getValues().achievedAmountPercentage) {
        calcAchievedAmount(getValues().targetAmount, getValues().achievedAmountPercentage)
      }
    }
  }

  const formatDate = (date: string) => {
    return formatDateShort(date, true) || '-'
  }

  useEffect(() => {
    setNewTargetForm(newTargetForm)
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (targetType === TargetModalTargetType.monetary) {
      trigger()
    }
  }, [targetType, trigger, unregister])

  return !!currentObjective ? (
    <>
      <TargetsTemplateSelect targetsTemplate={targetsTemplate} setTitle={setValue} clearErrors={clearErrors} replaceTasks={replace} />

      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <Controller
          name="title"
          control={control}
          rules={{ required: `${t('formError.generalRequired')}` }}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={hasError(errors, 'title', -1) ? 'has-error' : ''} style={{ width: '100%' }}>
                <TextArea
                  value={value || undefined}
                  onChange={onChange}
                  rows={1}
                  maxLength={MAX_CHARS}
                  style={{ width: '100%', lineHeight: 1.6 }}
                  placeholder={t('targetModal.titlePlaceholder')}
                  data-e2e-target-title
                />
              </div>
              <PerFlexRow align="middle" justify="space-between" className="fullWidth">
                <PerFlexRow justify="start">
                  <ErrorMessage
                    errors={errors}
                    name="title"
                    render={({ messages }) => {
                      return (
                        <PerText fontSize="xs" color="error">
                          {!!messages && !!messages.required && messages.required}
                        </PerText>
                      )
                    }}
                  />
                </PerFlexRow>
                <PerFlexRow justify="end">
                  <MaxCharactersWrapper usedCharacters={!!value ? value.length : 0} maxCharacters={MAX_CHARS} />
                </PerFlexRow>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>
      <PerFlexRow mt="s">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>
      <PerFlexRow direction="column">
        {fields.map((field, index) => {
          return (
            <React.Fragment key={index}>
              <PerFlexRow key={field.id || field.globalId} className="fullWidth per-target-modal--tasks-wrapper" align="middle" mt="s">
                <PerFlexRow
                  className={classNames({
                    'per-target-modal--tasks-checkbox top-fix': index === fields.length - 1,
                    'per-target-modal--tasks-checkbox': index !== fields.length,
                  })}
                >
                  <Controller
                    name={`keyResults.${index}.completed`}
                    control={control}
                    defaultValue={false}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <PerFlexRow className="per-formikKeyResultsItem">
                          <Tooltip title={t('targetModal.taskCheckboxTooltip')} placement="left">
                            <Checkbox checked={value} onChange={onChange} />
                          </Tooltip>
                        </PerFlexRow>
                      )
                    }}
                  />
                </PerFlexRow>
                <PerFlexRow className="per-target-modal--tasks-input">
                  <PerFlexRow className="fullWidth" align="middle" justify="start" noWrap>
                    <Controller
                      name={`keyResults.${index}.text`}
                      control={control}
                      rules={{
                        required: `${t('formError.generalRequired')}`,
                        minLength: { value: 2, message: `${t('formError.textLength')}` },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <div className={hasError(errors, 'text', index) ? 'has-error' : ''} style={{ width: '89%' }}>
                          <TextArea
                            value={value || undefined}
                            onChange={onChange}
                            rows={1}
                            style={{ width: '100%', lineHeight: 1.6 }}
                            onKeyPress={handleKeyPress}
                            onPaste={handleOnPaste}
                            placeholder={t('targetModal.taskPlaceholder')}
                            data-e2e-keyresult-input
                          />
                        </div>
                      )}
                    />
                    <PerFlexRow align="middle" justify="start">
                      {fields.length > 1 && (
                        <PerButtonCircle
                          icon="remove_stroke"
                          size="small"
                          variant="pastel"
                          color="secondary"
                          ml="xs"
                          onClick={() => remove(index)}
                          data-e2e-keyresult-button="remove"
                        />
                      )}
                      {index === fields.length - 1 && (
                        <PerButtonCircle
                          icon="add_stroke"
                          size="small"
                          variant="pastel"
                          color="success"
                          ml="xxs"
                          disabled={!!errors.keyResults}
                          onClick={() => {
                            append({ text: '' } as KeyResultDto)
                            trigger()
                          }}
                          data-e2e-keyresult-button="add"
                        />
                      )}
                    </PerFlexRow>
                  </PerFlexRow>
                  <PerFlexRow align="middle" justify="space-between" width="89%">
                    <PerFlexRow align="middle" justify="start">
                      <PerText fontSize="xs" color="error" mt="xxxs">
                        <ErrorMessage
                          errors={errors}
                          name={`keyResults.${index}.text`}
                          render={({ messages }) => {
                            return (
                              <>
                                <PerText fontSize="xs" color="error">
                                  {!!messages && !!messages.required && messages.required}
                                </PerText>
                                <PerText fontSize="xs" color="error">
                                  {!!messages && !!messages.minLength && messages.minLength}
                                </PerText>
                              </>
                            )
                          }}
                        />
                      </PerText>
                    </PerFlexRow>
                    <PerFlexRow align="middle" justify="end">
                      {index === fields.length - 1 && <TaskCharacterCounter control={control} />}
                    </PerFlexRow>
                  </PerFlexRow>
                </PerFlexRow>
              </PerFlexRow>
            </React.Fragment>
          )
        })}
      </PerFlexRow>
      <PerFlexRow mt="xl">
        <PerText appearance="tile-heading">{t('targetModal.notes')}</PerText>
      </PerFlexRow>
      <PerFlexRow direction="column" mt="m">
        <Controller
          name="taskNotes"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RichTextBox placeholder={t('targetModal.notesPlaceholder')} text={value || ''} onChange={onChange} />
          )}
        />
      </PerFlexRow>
      <Divider />
      <PerFlexRow>
        <PerText appearance="tile-heading">{t('targetModal.targetDetails')}</PerText>
      </PerFlexRow>
      <PerFlexRow mt="m">
        <Controller
          name="targetType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Radio.Group
              value={value}
              onChange={(e) => {
                onChange(e)
                setTargetType(e.target.value)
              }}
            >
              <Radio value={TargetModalTargetType.development} data-e2e-new-target-radio-development>
                <PerText fontWeight="bold" display="inline-block">
                  {t('targetModal.developmentTarget')}
                </PerText>
              </Radio>
              <Tooltip title={modalProps.disabledMonetaryButtonTooltip}>
                <Radio value={TargetModalTargetType.monetary} disabled={!modalProps.allowedPastTargets} data-e2e-new-target-radio-monetary>
                  <PerText fontWeight="bold" display="inline-block">
                    {t('targetModal.monetaryTarget')}
                  </PerText>
                </Radio>
              </Tooltip>
            </Radio.Group>
          )}
        />
      </PerFlexRow>
      <PerFlexRow mt="l">
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.startDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.start)}</PerText>
        </Col>
        <Col span={1} />
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.dueDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.due)}</PerText>
        </Col>
      </PerFlexRow>
      {targetType === TargetModalTargetType.monetary && (
        <Controller
          name="targetAmount"
          control={control}
          rules={{
            required: `${t('formError.generalRequired')}`,
            max: { value: calcThreshold(), message: `${t('formError.targetAmount')}` },
            deps: ['achievedAmount'],
          }}
          render={({ field: { onChange, value } }) => (
            <>
              <PerFlexRow mt="l" align="middle">
                <Col span={8}>
                  <PerText appearance="label-small" mb="xxs">
                    {t('targetModal.targetAmount')}
                    {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && <TargetsModalAsterisk />}
                  </PerText>
                  <div className={hasError(errors, 'targetAmount', -1) ? 'has-error' : ''} style={{ width: '100%' }}>
                    <PerInputNumber
                      type="currency"
                      value={value as number | undefined}
                      onChange={(value) => {
                        onChange(value)
                        handleTargetAmountChange()
                      }}
                      width={'100%'}
                      currency={currency}
                      placeholder="0"
                      min={0}
                      timezoneAndLocale={user}
                      data-e2e-monetary-input
                    />
                  </div>
                </Col>
                <Col span={1} />
                <Col span={8}>
                  <PerFlexRow direction="column" mt="m">
                    <PerFlexRow>
                      <PerText appearance="label-small">{t('targetModal.annualBonus')}</PerText>
                      <PerText appearance="label-small" color="default" ml="xxxs">
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        {isDefined(modalProps.targetBonus) && formatCurrency(modalProps.targetBonus!, currency, true)}
                      </PerText>
                    </PerFlexRow>
                    <PerFlexRow>
                      <PerText appearance="label-small">{t('targetModal.remainingBonus')}</PerText>
                      <PerText appearance="label-small" color={calcRemainingBonus(value) < 0 ? 'error' : 'default'} ml="xxxs">
                        {!!modalProps.targetBonus && formatCurrency(calcRemainingBonus(value), currency, true)}
                      </PerText>
                    </PerFlexRow>
                  </PerFlexRow>
                </Col>
              </PerFlexRow>
              <div className="per-target-modal--field-error">
                <ErrorMessage
                  errors={errors}
                  name="targetAmount"
                  render={({ messages }) => {
                    return (
                      <>
                        <PerText fontSize="xs" color="error">
                          {!!messages && !!messages.required && messages.required}
                        </PerText>
                        <PerText fontSize="xs" color="error">
                          {!!messages && !!messages.max && messages.max}
                        </PerText>
                      </>
                    )
                  }}
                />
              </div>
              {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && (
                <TargetsModalMessage message={t(`targetModal.targetMessage.${Country.Pol}`)} />
              )}
            </>
          )}
        />
      )}
      <PerFlexRow mt="l" mb="l" align="middle">
        <Controller
          name="markedAsDone"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <>
              <PerFlexRow className="per-formikKeyResultsItem">
                <Checkbox
                  checked={value}
                  onChange={(e) => {
                    onChange(e)
                    trigger()
                  }}
                  data-testid="markAsDoneCheckbox"
                  disabled={true}
                >
                  {t('targetModal.markAsDone')}
                </Checkbox>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>
      {targetType === TargetModalTargetType.monetary && (
        <PerFlexRow mt="xs" align="middle">
          <Col span={8}>
            <PerText appearance="label-small" mb="xxs">
              {t('targetModal.achievedAmount')}
            </PerText>
            <Controller
              name="achievedAmount"
              control={control}
              rules={{
                required: `${t('targetModal.achievedAmountRequired')}`,
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <div className={hasError(errors, 'achievedAmount', -1) ? 'has-error' : ''} style={{ width: '100%' }}>
                    <PerInputNumber
                      type="currency"
                      value={value as number | undefined}
                      onChange={(value) => {
                        onChange(value)
                        calcAchievedAmountPercentage(value, getValues().targetAmount)
                      }}
                      width={'100%'}
                      currency={currency}
                      min={0}
                      timezoneAndLocale={user}
                      data-e2e-achieved-amount
                    />
                  </div>
                  <div className="per-target-modal--field-error">
                    <ErrorMessage
                      errors={errors}
                      name="achievedAmount"
                      render={({ messages }) => {
                        return (
                          <>
                            <PerText fontSize="xs" color="error">
                              {!!messages && !!messages.required && messages.required}
                            </PerText>
                          </>
                        )
                      }}
                    />
                  </div>
                </>
              )}
            />
          </Col>
          <Col span={1} />
          <Col span={8}>
            <PerFlexRow mt="m" align="middle">
              <Controller
                name="achievedAmountPercentage"
                control={control}
                rules={{ deps: ['achievedAmount'] }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <div>
                      <PerInputNumber
                        type="percentage"
                        value={value}
                        onChange={(value) => {
                          onChange(value)
                          calcAchievedAmount(getValues().targetAmount, value)
                        }}
                        width={'100%'}
                        min={0}
                        percentageHasDecimal
                        timezoneAndLocale={user}
                        data-e2e-achieved-amount-percentage
                      />
                    </div>
                    <div className="per-target-modal--field-error"></div>
                  </>
                )}
              />
            </PerFlexRow>
          </Col>
        </PerFlexRow>
      )}
      <PerSpacer mt="m" />
      <TargetsModalAttachment showDropZone />
    </>
  ) : null
}
